import { useRef } from "react"
import useEmblaCarousel from "embla-carousel-react"
import { AdvisorCard } from "./advisor-card"
import type { Advisor } from "../types"

interface AdvisorCarouselProps {
  advisors: Advisor[]
  onCardClick?: (advisor: Advisor) => void
  onButtonClick?: (advisor: Advisor) => void
}

const DRAG_THRESHOLD = 10

/**
 * Draggable horizontal carousel of advisor cards (embla). A pointer-movement
 * guard suppresses the card click after a drag, mirroring the source behavior.
 */
export function AdvisorCarousel({ advisors, onCardClick, onButtonClick }: AdvisorCarouselProps) {
  const [emblaRef] = useEmblaCarousel({ align: "start", loop: true, containScroll: false })

  const start = useRef({ x: 0, y: 0 })
  const dragged = useRef(false)

  const handlePointerDown = (e: React.PointerEvent) => {
    dragged.current = false
    start.current = { x: e.clientX, y: e.clientY }
  }

  const handlePointerMove = (e: React.PointerEvent) => {
    if (
      Math.abs(e.clientX - start.current.x) > DRAG_THRESHOLD ||
      Math.abs(e.clientY - start.current.y) > DRAG_THRESHOLD
    ) {
      dragged.current = true
    }
  }

  const guardedClick = (advisor: Advisor) => {
    if (!dragged.current) onCardClick?.(advisor)
  }

  return (
    <div
      ref={emblaRef}
      className="mt-4 overflow-hidden"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
    >
      <div className="flex gap-5 pl-4">
        {advisors.map((advisor, index) => (
          <div key={`${advisor.assistantId}-${index}`} className="flex-[0_0_240px]">
            <AdvisorCard
              advisor={advisor}
              onClick={() => guardedClick(advisor)}
              onButtonClick={() => onButtonClick?.(advisor)}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
